import { z } from 'zod';

import { BlogPostStatusInput } from '@nucleus-commerce/api-client/admin';

import { seoOverrideSchema } from '@/features/seo';

export const HANDLE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
export const TAG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const MAX_TAGS = 20;
export const TAG_MAX_LENGTH = 40;

/** `/blog/category` is the category listing, so no post may take that handle. */
export const RESERVED_POST_HANDLES: ReadonlySet<string> = new Set(['category']);

const LOCAL_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

export const isTag = (value: string) => value.length <= TAG_MAX_LENGTH && TAG.test(value);

export const handleSchema = z
  .string()
  .trim()
  .min(1, { error: 'blog:validation.handleRequired' })
  .max(120, { error: 'blog:validation.handleLength' })
  .regex(HANDLE, { error: 'blog:validation.handleFormat' });

/**
 * `publishedAt` is the `datetime-local` value on the store's wall clock ("" = now when
 * published); the mapper converts it with the store timezone.
 */
export const blogPostFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { error: 'blog:validation.titleRequired' })
    .max(200, { error: 'blog:validation.titleLength' }),
  handle: handleSchema.refine((handle) => !RESERVED_POST_HANDLES.has(handle), {
    error: 'blog:validation.handleReserved',
  }),
  excerpt: z.string().trim().max(500, { error: 'blog:validation.excerptLength' }),
  bodyHtml: z.string().max(200_000, { error: 'blog:validation.bodyLength' }),
  featuredMediaId: z.string().nullable(),
  authorName: z.string().trim().max(120, { error: 'blog:validation.authorLength' }),
  /** "" is no category. */
  categoryId: z.string(),
  tags: z
    .array(z.string())
    .max(MAX_TAGS, { error: 'blog:validation.tagsMax' })
    .refine((tags) => tags.every(isTag), { error: 'blog:validation.tagFormat' }),
  status: z.enum(BlogPostStatusInput),
  publishedAt: z.string().refine((value) => value === '' || LOCAL_DATE_TIME.test(value), {
    error: 'blog:validation.publishedAt',
  }),
  seo: seoOverrideSchema,
});

export type BlogPostFormInput = z.input<typeof blogPostFormSchema>;
export type BlogPostFormOutput = z.output<typeof blogPostFormSchema>;
